import React, { useState, useMemo, useCallback } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import {
  useGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
} from '@/store/api';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';

import { PaymentModalFooter } from '@/components/residents/payments/mobile/makePayment';
import { ManageWalletContent } from './content/ManageWalletContent';
import { AddPaymentMethodContent } from './content/AddPaymentMethodContent';
import { CreditCardFormContent } from './content/CreditCardFormContent';
import { ACHFormContent } from './content/ACHFormContent';

type WalletScreen = 'manage' | 'add' | 'cardForm' | 'achForm';

const SCREEN_TITLES: Record<WalletScreen, string> = {
  manage: 'Manage my wallet',
  add: 'Add a payment method',
  cardForm: 'Credit or debit card details',
  achForm: 'ACH details',
};

type MobileManageWalletScreenProps = StackScreenProps<RootStackParamList, 'ManageWallet'>;

const parseExpiry = (expiry: string | undefined): { month?: number; year?: number } => {
  if (!expiry) return {};
  let mm: number, yy: number;
  if (expiry.includes('/')) {
    const [month, year] = expiry.split('/');
    mm = Number(month);
    yy = Number(year);
  } else if (expiry.length === 4) {
    mm = Number(expiry.substring(0, 2));
    yy = Number(expiry.substring(2));
  } else {
    return {};
  }
  const fullYear = yy < 100 ? 2000 + yy : yy;
  return { month: mm, year: fullYear };
};

export const MobileManageWalletScreen: React.FC = () => {
  const route = useRoute<MobileManageWalletScreenProps['route']>();
  const navigation = useNavigation<MobileManageWalletScreenProps['navigation']>();

  const initialScreen: WalletScreen = route.params?.initialScreen === 'add' ? 'add' : 'manage';

  const { data: methodsData, refetch: refetchMethods } = useGetPaymentMethodsQuery();
  const { data: feeData } = useGetFeeStructureQuery();

  const [screenHistory, setScreenHistory] = useState<WalletScreen[]>([initialScreen]);
  const currentScreen = screenHistory[screenHistory.length - 1];

  const paymentMethods: PaymentMethod[] = useMemo(() => {
    const methods = (methodsData as any)?.response || [];
    return methods.map((m: any) => {
      const parsedExpiry = parseExpiry(m.expiry);
      return {
        id: m.id,
        sourceId: m.sourceId || m.id,
        type: m.type === 'card' ? 'card' : 'ach',
        name: m.name,
        last4: m.last4,
        brand: m.brand || m.accttype,
        bankName: m.bankName || m.name,
        isExpired: m.isExpired,
        expiryMonth: m.expiryMonth || parsedExpiry.month,
        expiryYear: m.expiryYear || parsedExpiry.year,
        isDefault: m.isDefault,
      };
    });
  }, [methodsData]);

  const feeStructure: FeeStructure = useMemo(() => {
    const fees = (feeData as any)?.response || {};
    return {
      creditCard: fees.creditCard || '3.39%',
      debitCard: fees.debitCard || '2%',
      bankAccount: fees.bankAccount || '$3.99',
    };
  }, [feeData]);

  const navigateTo = useCallback((screen: WalletScreen) => {
    setScreenHistory(prev => [...prev, screen]);
  }, []);

  const goBack = useCallback(() => {
    if (screenHistory.length > 1) {
      setScreenHistory(prev => prev.slice(0, -1));
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [screenHistory, navigation]);

  const resetToManage = useCallback(() => {
    setScreenHistory(['manage']);
  }, []);

  const handleClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleMethodAdded = useCallback(() => {
    refetchMethods();
    resetToManage();
  }, [refetchMethods, resetToManage]);

  const handleMethodsUpdated = useCallback(() => {
    refetchMethods();
  }, [refetchMethods]);

  const renderContent = () => {
    switch (currentScreen) {
      case 'manage':
        return (
          <ManageWalletContent
            paymentMethods={paymentMethods}
            onMethodsUpdated={handleMethodsUpdated}
          />
        );

      case 'add':
        return (
          <AddPaymentMethodContent
            onSelectCard={() => navigateTo('cardForm')}
            onSelectACH={() => navigateTo('achForm')}
            feeStructure={feeStructure}
          />
        );

      case 'cardForm':
        return (
          <CreditCardFormContent
            onCardAdded={handleMethodAdded}
            onBack={goBack}
          />
        );

      case 'achForm':
        return (
          <ACHFormContent
            onACHAdded={handleMethodAdded}
            onBack={goBack}
          />
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentScreen) {
      case 'manage':
        return (
          <PaymentModalFooter
            onCancel={handleClose}
            onProceed={handleClose}
            proceedText="Done"
            showCancel={false}
            variant="single"
          />
        );

      case 'add':
        return (
          <PaymentModalFooter
            onCancel={goBack}
            onProceed={goBack}
            proceedText="Cancel"
            showCancel={false}
            variant="single"
          />
        );

      case 'cardForm':
      case 'achForm':
        return null;

      default:
        return null;
    }
  };

  const footerContent = renderFooter();
  const hasEmbeddedFooter = currentScreen === 'cardForm' || currentScreen === 'achForm';
  const currentTitle = SCREEN_TITLES[currentScreen];

  return (
    <>
      <UnifiedModal
        visible={true}
        variant="fullscreen"
        animateEntry={false}
        onClose={handleClose}
        onRequestClose={handleClose}
        title={currentTitle || undefined}
        centerTitle
        showHeader
        footer={hasEmbeddedFooter ? undefined : footerContent}
        footerStyle={hasEmbeddedFooter ? undefined : footerPaddingReset}
        avoidKeyboard
        scrollable
        contentContainerStyle={scrollContentOverride}
      >
        {renderContent()}
      </UnifiedModal>

    </>
  );
};

const footerPaddingReset = { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0 };
const scrollContentOverride = { paddingHorizontal: 0, paddingBottom: 0, flexGrow: 1 };

export default MobileManageWalletScreen;
